/**
 * The call archive's arithmetic, kept apart from the page so it can be tested without one.
 *
 * Everything here is pure: the filters as a query string, which way a call faced, how long
 * it rang and how long it ran, which recording comes next, and whether a playback link is
 * still worth using. The page and the player own the state; this file owns the rules.
 */

import type { Schema } from '@taxi/api-client';

// From its own file, not the barrel: the client tests compile this without the DOM, and the
// barrel brings every primitive that touches it.
import type { Tone } from '../ui/Pill';

export type CallArchive = Schema<'CallArchive'>;

export type ArchivedCall = Schema<'ArchivedCall'>;

export type CallTotals = Schema<'CallTotals'>;

export type RecordingAccess = Schema<'RecordingAccessView'>;

export type RecordingLink = Schema<'RecordingLink'>;

// --- Filters -------------------------------------------------------------------

/**
 * The outcomes the filter offers, as groups rather than the log's six words.
 *
 * Somebody reviewing calls asks "the ones we missed", and a missed call and an abandoned one
 * are the same failure seen from the two ends of the line. A refused caller was blocked on
 * purpose and a failed call never connected, so neither belongs with either.
 */
export const outcomeGroups = {
  all: null,
  answered: ['Answered'],
  unanswered: ['Missed', 'Abandoned'],
  other: ['Refused', 'Failed'],
} as const satisfies Record<string, readonly string[] | null>;

export type OutcomeGroup = keyof typeof outcomeGroups;

export type DirectionFilter = 'all' | 'Inbound' | 'Outbound';

export interface CallFilters {
  /** A number typed any way, or an order's reference. The server works out which. */
  text: string;
  direction: DirectionFilter;
  outcome: OutcomeGroup;
  /** The dispatcher's account, or null for everybody. */
  dispatcher: string | null;
  recorded: boolean;
}

export const NO_FILTERS: CallFilters = {
  text: '',
  direction: 'all',
  outcome: 'all',
  dispatcher: null,
  recorded: false,
};

/** Whether anything narrows the archive beyond the period. */
export function isFiltered(filters: CallFilters): boolean {
  return (
    filters.text.trim() !== '' ||
    filters.direction !== 'all' ||
    filters.outcome !== 'all' ||
    filters.dispatcher !== null ||
    filters.recorded
  );
}

/**
 * The filters as query parameters, to follow the period's.
 *
 * Every value is encoded, because the search box is free text — a `+` from an international
 * number would otherwise arrive as a space and match nobody. A filter at its default is left
 * out rather than sent empty, so the address a request was made to says only what narrowed it.
 *
 * @param page From nought. The first page is left out like any other default.
 */
export function filterQuery(filters: CallFilters, page = 0): string {
  const parts: string[] = [];
  const text = filters.text.trim();
  const outcomes = outcomeGroups[filters.outcome];

  if (text !== '') parts.push(`q=${encodeURIComponent(text)}`);
  if (filters.direction !== 'all') parts.push(`direction=${filters.direction}`);
  if (outcomes !== null) parts.push(`outcome=${outcomes.join(',')}`);
  if (filters.dispatcher !== null) parts.push(`dispatcher=${filters.dispatcher}`);
  if (filters.recorded) parts.push('recorded=true');
  if (page > 0) parts.push(`page=${page}`);

  return parts.map((part) => `&${part}`).join('');
}

// --- One call ------------------------------------------------------------------

/** The customer's end of the line: who rang in, or who the firm rang. */
export function otherParty(call: Pick<ArchivedCall, 'direction' | 'from' | 'to'>): string | null {
  return (call.direction === 'Outbound' ? call.to : call.from) ?? null;
}

/** Seconds from the first ring to somebody picking up. Null when nobody did. */
export function ringSeconds(call: Pick<ArchivedCall, 'startedAt' | 'answeredAt'>): number | null {
  return call.answeredAt ? secondsBetween(call.startedAt, call.answeredAt) : null;
}

/** Seconds of conversation. Null for a call nobody picked up, or one still going. */
export function talkSeconds(call: Pick<ArchivedCall, 'answeredAt' | 'endedAt'>): number | null {
  return call.answeredAt && call.endedAt ? secondsBetween(call.answeredAt, call.endedAt) : null;
}

/**
 * Never negative: the events behind a call are written by more than one process, and two
 * clocks half a second apart would otherwise put "−1 s" in front of somebody.
 */
function secondsBetween(from: string, to: string): number {
  return Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 1000));
}

/**
 * The outcome's colour. An unanswered call is only red while somebody still owes it a call
 * back; once it has been dealt with it is history, and a page of red history is a page
 * nobody reads for the red that matters.
 */
export function outcomeTone(call: Pick<ArchivedCall, 'outcome' | 'needsCallingBack'>): Tone {
  switch (call.outcome) {
    case 'Answered':
      return 'ok';
    case 'Missed':
    case 'Abandoned':
      return call.needsCallingBack ? 'danger' : 'warn';
    case 'InProgress':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Whole days until the retention sweep deletes a recording, never below nought. */
export function daysLeft(expiresAt: string | null | undefined, now: number): number | null {
  if (!expiresAt) return null;

  return Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 86_400_000));
}

// --- The player ----------------------------------------------------------------

/**
 * A position on the scrubber: `1:05`, or `1:02:05` past the hour.
 *
 * Not the tenant's duration format, which reads `1m 05s` and suits a table. A player's clock
 * changes every second under somebody's eye, and it is read against the one beside it.
 */
export function clock(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const rest = String(whole % 60).padStart(2, '0');

  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
}

/** A link and when it was handed over, in the browser's milliseconds. */
export interface HeldLink {
  url: string;
  fetchedAt: number;
  expiresInSeconds: number;
}

/**
 * Whether a link is still worth giving to the player.
 *
 * With a margin, because a link with three seconds left is handed to a player that then asks
 * for the next range a few seconds later and is refused — the silent failure the issue was
 * about. Asking for a fresh one costs a request and a line in the trail; playing a dead one
 * costs somebody pressing play and hearing nothing.
 */
export function isFresh(link: HeldLink | null, now: number, marginSeconds = 20): boolean {
  if (link === null) return false;

  return now < link.fetchedAt + (link.expiresInSeconds - marginSeconds) * 1000;
}

/**
 * The recording before or after this one, in the order the table shows them.
 *
 * Calls without a recording are stepped over: somebody going through the morning's calls
 * one after another wants the next thing they can hear, not a row they have to skip by hand.
 *
 * @param step 1 for the next row down, -1 for the one above.
 */
export function neighbour<T extends Pick<ArchivedCall, 'id' | 'hasRecording'>>(
  calls: readonly T[],
  currentId: string | null,
  step: 1 | -1,
): T | null {
  const at = calls.findIndex((call) => call.id === currentId);

  if (at < 0) return null;

  for (let index = at + step; index >= 0 && index < calls.length; index += step) {
    if (calls[index].hasRecording) return calls[index];
  }

  return null;
}

/** Playback speeds, in the order the button steps through them. */
export const speeds = [1, 1.25, 1.5, 2] as const;

/** The speed after this one, wrapping round to normal. */
export function nextSpeed(rate: number): number {
  const at = speeds.findIndex((speed) => speed === rate);

  return speeds[(at + 1) % speeds.length];
}
